import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, useTheme } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { getSharedFormStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { formApi, formsApi } from 'app/services/api';

import { FeatureFlag } from 'constants/featureFlags';
import useForm from 'hooks/useForm';
import useFormsInfoLoader from 'hooks/useFormsInfoLoader';
import { getServerErrorMessage } from 'utils/error';
import { getConfigInfo } from 'utils/loanManagement';
import { logger } from 'utils/logger';

import { selectFormsInfoListOptions } from 'store/forms/selectors';
import { showErrorMessageAction } from 'store/global/actions';
import { selectFeatureFlagByKey } from 'store/global/selectors';
import {
  selectApplicationIdFromDeal,
  selectCommonInfoLoanApplication,
  selectLoanApplicationBorrowerOptions,
} from 'store/loanApplications/selectors';

import { DocumentsTestIds } from '../Documents.testIds';

import { getBorrowerField, getLoanFormIdField } from './LoanFormGenerationForm.config';
import { FORM_GENERATION_FIELD_NAME } from './LoanFormGenerationForm.constants';
import {
  formGenerationControlsStyles,
  formGenerationLayoutStyles,
} from './LoanFormGenerationForm.styles';
import LoanFormGenerationPreviewPanel from './LoanFormGenerationPreviewPanel';

const LoanFormGenerationForm = () => {
  const theme = useTheme();
  const sharedClasses = useMemo(() => getSharedFormStyles(theme), [theme]);

  const dispatch = useDispatch();

  useFormsInfoLoader();

  const formsOptions = useSelector(selectFormsInfoListOptions);
  const borrowerOptions = useSelector(selectLoanApplicationBorrowerOptions);
  const { loanNumber } = useSelector(selectCommonInfoLoanApplication);
  const applicationId = useSelector(selectApplicationIdFromDeal);
  const isFormsConsolidationEnabled = useSelector(
    selectFeatureFlagByKey(FeatureFlag.FormsConsolidationEnabled),
  );

  const formIdConfig = useMemo(() => getLoanFormIdField(formsOptions), [formsOptions]);
  const borrowerConfig = useMemo(() => getBorrowerField(borrowerOptions), [borrowerOptions]);

  const { initialFormValues: formIdInitialValues, schema: formIdSchema } = useMemo(
    () => getConfigInfo({ applicationData: {}, config: formIdConfig }),
    [formIdConfig],
  );

  const { initialFormValues: borrowerInitialValues, schema: borrowerSchema } = useMemo(
    () => getConfigInfo({ applicationData: {}, config: borrowerConfig }),
    [borrowerConfig],
  );

  const mergedInitialValues = useMemo(
    () => ({ ...formIdInitialValues, ...borrowerInitialValues }),
    [formIdInitialValues, borrowerInitialValues],
  );

  // Yup schemas are class instances — object-spreading them drops their prototype
  // methods (validateAt/validate), so merge via Yup's concat to keep a real schema.
  const mergedSchema = useMemo(
    () => formIdSchema.concat(borrowerSchema),
    [formIdSchema, borrowerSchema],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    validateForm,
    validateField,
  } = useForm(mergedInitialValues);

  const generatedFormBlobRef = useRef(null);
  const contentDispositionRef = useRef('');

  const [generationCount, setGenerationCount] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    setSchema(mergedSchema);
  }, [setSchema, mergedSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      const formId = form[FORM_GENERATION_FIELD_NAME.FORM_ID]?.value;

      setIsGenerating(true);

      try {
        const { data, headers } = isFormsConsolidationEnabled
          ? await formsApi.getGeneratedFormByApplicationId({ formId, applicationId })
          : await formApi.getGeneratedFormByLoanNumber({ formId, loanNumber });

        generatedFormBlobRef.current = data;
        contentDispositionRef.current = headers['content-disposition'];

        setGenerationCount(count => count + 1);
      } catch (err) {
        logger.error(err);

        const serverMessage = await getServerErrorMessage(err);

        dispatch(
          showErrorMessageAction({ message: serverMessage || "Couldn't generate form", err }),
        );
      } finally {
        setIsGenerating(false);
      }
    },
    [validateForm, form, isFormsConsolidationEnabled, applicationId, loanNumber, dispatch],
  );

  const isFormIdMissing = !form?.[FORM_GENERATION_FIELD_NAME.FORM_ID];
  const isSubmitDisabled = isFormIdMissing || isGenerating;

  return (
    <form noValidate onSubmit={onSubmit}>
      <Box sx={formGenerationLayoutStyles}>
        <Box sx={formGenerationControlsStyles}>
          <FormGenerator
            config={formIdConfig}
            form={form}
            handleChange={handleChange}
            setInForm={setInForm}
            validateField={validateField}
            validationErrors={validationErrors}
          />

          <FormGenerator
            config={borrowerConfig}
            form={form}
            handleChange={handleChange}
            setInForm={setInForm}
            validateField={validateField}
            validationErrors={validationErrors}
          />

          <Button
            color="primary"
            data-testid={DocumentsTestIds.LOAN_FORM_GENERATION_FORM_SUBMIT_BUTTON}
            disabled={isSubmitDisabled}
            sx={sharedClasses.submitButton}
            type="submit"
            variant="contained"
          >
            Generate Form
          </Button>
        </Box>

        <LoanFormGenerationPreviewPanel
          contentDispositionRef={contentDispositionRef}
          generatedFormBlobRef={generatedFormBlobRef}
          generationCount={generationCount}
          loanNumber={loanNumber}
        />
      </Box>
    </form>
  );
};

export default memo(LoanFormGenerationForm);
